import * as React from 'react';

import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV2';
import { LicenseInfo } from '@mui/x-license';
import { muiXTelemetrySettings } from '@mui/x-telemetry';
import {
  getLoggedInAs,
  parseJwt,
  refreshToken,
} from '@realadvisor/auth-client';
import { hasActiveModuleGrant } from '@realadvisor/constants';
import { ErrorReporting } from '@realadvisor/error';
import { GoogleKeyProvider } from '@realadvisor/google-maps';
import { useConstant } from '@realadvisor/hooks';
import { ImageProvider } from '@realadvisor/image';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import { Layout } from '@app/components/layout/Layout';
import { SuspenseFallback } from '@app/components/SuspenseFallback';
import { GatewayLayout } from '@app/GatewayLayout';
import { LegacyLogin, LegacyPlans } from '@app/LegacyBoundaries';
import { errorsEmitter } from '@graphql/ApolloHasuraWrapper';
import { AdminRoute } from '@modules/_shared/components/AdminRoute';
import { SchemaInitializer } from '@modules/_shared/components/filters/SchemaProvider';
import { ModuleRoute } from '@modules/_shared/components/ModuleRoute';
import { ProspectionMapRoute } from '@modules/_shared/components/ProspectionMapRoute';
import { RATenantRoute } from '@modules/_shared/components/RATenantRoute';
import {
  type Me,
  getActiveModuleIds,
  useAppData,
} from '@modules/_shared/providers/AppDataProvider';
import { CompassLoader } from '@modules/_shared/shared/components/CompassLoader';
import { isNeitherAdminNorBroker } from '@modules/_shared/utils/access';
import { LoginEmail } from '@modules/auth/LoginEmail';
import { NoAccess } from '@modules/auth/NoAccess';
import { ClaimAgency } from '@modules/claim/ClaimAgency';
import { ClaimProfile } from '@modules/claim/ClaimProfile';
import { BookADemoBanner } from '@modules/dashboard/BookADemoBanner';
import Dashboard from '@modules/dashboard/Dashboard';
import InvitationAccept from '@modules/invitation/InvitationAccept';
import Leads from '@modules/leads/Leads';
import Listings from '@modules/listings/Listings';
import { readOnboardingCompletionForUser } from '@modules/onboarding/onboardingCompletionState';
import { OnboardingStatusBanner } from '@modules/onboarding/OnboardingStatusBanner';
import { PlansTrialing } from '@modules/plans/PlansTrialing';
import { ProductUpdatesProvider } from '@modules/product-updates/ProductUpdatesProvider';
import CMAReportsList from '@modules/reports/cma-reports/CMAReportsList';
import { SelectTenant } from '@modules/select-tenant/SelectTenant';
import { TakedownAgency } from '@modules/takedown/TakedownAgency';
import { TakedownAgent } from '@modules/takedown/TakedownAgent';
import { Teams } from '@modules/teams/Teams';
import { Transactions } from '@modules/transactions/Transactions';
import Users from '@modules/users/Users';
import { useAnalytics } from '@shared/analytics';
import { GOOGLE_MAPS_TOKEN, isProduction } from '@shared/config';
import {
  MODULES_BROKERAGE,
  MODULES_CMA_REPORTS,
  MODULES_LEADS,
  MODULES_VALUATION_LINK,
  MODULES_VISIBILITY,
} from '@shared/constants/module';
import { LocaleProvider, useLocale } from '@shared/hooks/locale';
import {
  extractLanguageFromUrl,
  fallbackLanguage,
  getLanguage,
  replacePathnameLanguage,
} from '@shared/LocaleContext';
import { NavigationProvider } from '@shared/NavigationContext';
import { StyleProvider } from '@shared/theme/theme';
import { getAfterLoginDestination } from '@shared/utils/afterLoginDestination';
import { lazyRetry } from '@shared/utils/lazyRetry';
import {
  LEGACY_PROSPECTION_MAP_PATH,
  PROSPECTION_MAP_V2_PATH,
} from '@shared/utils/prospectionMapAccess';
import { getTenantSlugStageAndSubdomainFromHostname } from '@shared/utils/tenant';

const ChatOAuthConsent = React.lazy(() =>
  lazyRetry(() =>
    import('../modules/settings/chat-connect/ChatOAuthConsent').then(m => ({
      default: m.ChatOAuthConsent,
    })),
  ),
);
const LegacyRoutes = React.lazy(() =>
  lazyRetry(() => import('./LegacyRoutesBoundary')),
);
const V2Routes = React.lazy(() =>
  lazyRetry(() => import('./routes/ModernRoutes')),
);
const BuyerLeads = React.lazy(() =>
  lazyRetry(() => import('@modules/buyer-leads/BuyerLeads')),
);
const ProspectionMap = React.lazy(() =>
  lazyRetry(() => import('@modules/maps/ProspectionMap')),
);
const ProspectionMapV2 = React.lazy(() =>
  lazyRetry(() => import('@modules/prospection-map/ProspectionMapV2')),
);
const PerformanceActivities = React.lazy(() =>
  lazyRetry(() => import('@modules/performance/activities/Activities')),
);

// The prospection maps used to live under /v2. Links to the old paths are
// already out there — n8n's new-leads emails point at /v2/maps?date=… — so
// they keep working, query string included, for users who are not admins.
const RedirectKeepingQuery: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: to, search: location.search, hash: location.hash }}
      replace
    />
  );
};
const SettingsRoutes = React.lazy(() =>
  lazyRetry(() => import('@modules/settings/SettingsRoutes')),
);
const OnboardingWizard = React.lazy(() =>
  lazyRetry(() =>
    import('@modules/onboarding/OnboardingWizard').then(module => ({
      default: module.OnboardingWizard,
    })),
  ),
);
const CMAReportEditor = React.lazy(() =>
  lazyRetry(
    () =>
      import('@modules/reports/cma-reports/CMAReportEditor/CMAReportEditor'),
  ),
);
const ComparableListings = React.lazy(() =>
  lazyRetry(() => import('@modules/comparable-listings/ComparableListings')),
);
const BrochureReportEditor = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        '@modules/reports/brochure-report/BrochureReportEditor/BrochureReportEditor'
      ),
  ),
);
const VisibilityDashboard = React.lazy(() =>
  lazyRetry(() => import('@modules/visibility-dashboard/VisibilityDashboard')),
);
const VisibilityTransactions = React.lazy(() =>
  lazyRetry(
    () => import('@modules/visibility-transactions/VisibilityTransactions'),
  ),
);
const VisibilityStats = React.lazy(() =>
  lazyRetry(() => import('@modules/visibility-stats/VisibilityStats')),
);
const VisibilityInbox = React.lazy(() =>
  lazyRetry(() => import('@modules/visibility-inbox/VisibilityInbox')),
);
const VisibilityReviews = React.lazy(() =>
  lazyRetry(() => import('@modules/visibility-reviews/VisibilityReviews')),
);
const VisibilityTeam = React.lazy(() =>
  lazyRetry(() => import('@modules/visibility-team/VisibilityTeam')),
);
const Offers = React.lazy(() =>
  lazyRetry(() => import('@modules/offers/Offers')),
);
const Enquiries = React.lazy(() =>
  lazyRetry(() => import('@modules/enquiries/Enquiries')),
);
const PropertySearches = React.lazy(() =>
  lazyRetry(() => import('@modules/property-searches/PropertySearches')),
);
const Reviews = React.lazy(() =>
  lazyRetry(() => import('@modules/reviews/Reviews')),
);
const Organisations = React.lazy(() =>
  lazyRetry(() => import('@modules/organisations/Organisations')),
);
const Developments = React.lazy(() =>
  lazyRetry(() => import('@modules/developments/Developments')),
);
const MLS = React.lazy(() => lazyRetry(() => import('@modules/mls/MLS')));
const ValuationLinks = React.lazy(() =>
  lazyRetry(() => import('@modules/valuation-links/ValuationLinks')),
);
const DocsRoute = React.lazy(() =>
  lazyRetry(() =>
    import('../docs/DocsPage').then(m => ({ default: m.DocsRoute })),
  ),
);
const WhatsNewPage = React.lazy(() =>
  lazyRetry(() =>
    import('@modules/product-updates/releases/WhatsNewPage').then(m => ({
      default: m.WhatsNewPage,
    })),
  ),
);

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

// Module-level guard against Apollo-triggered re-identifies. The CRM's
// `me` object re-references on every cache flush, so this effect
// previously fired `CRM.UserDetails` many times per session and — once
// the rename from `id` to `user_id` below is deployed — each push maps
// to a POST /i beacon. Dedup here keeps identify_events to ~1 per login.
let crmLastIdentifiedUserId: string | null = null;

// Report to Google Tag Manager
const GTMReportUserDetails = () => {
  const { me } = useAppData();

  React.useEffect(() => {
    const loggedInAs = getLoggedInAs();
    const userId = me?.id ?? null;

    if (userId != null && userId === crmLastIdentifiedUserId) {
      return;
    }
    if (userId == null) {
      crmLastIdentifiedUserId = null;
    } else {
      crmLastIdentifiedUserId = userId;
    }

    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({
      event: 'CRM.UserDetails',
      tenantId: me?.tenant_id ?? null,
      // The GTM `Analytics - Identify - CRM.UserDetails` tag expects
      // user_id / crm_user_id / userId — it does not read the legacy
      // `id` key, so every prior push was silently dropped in GTM. Keep
      // `id` as an alias for any other consumers (Intercom, etc.) that
      // might still read it.
      user_id: userId,
      id: userId,
      email:
        me?.emails?.find(({ primary }) => primary)?.email ??
        me?.emails?.[0]?.email ??
        null,
      firstName: me?.first_name ?? null,
      lastName: me?.last_name ?? null,
      organisationName: me?.organisation?.name ?? null,
      organisationId: me?.organisation?.id ?? null,
      teamId: me?.default_team?.id ?? null,
      teamName: me?.default_team?.name ?? null,
      phoneNumber: me?.phone_numbers?.find(p => p.primary)?.number ?? null,
      avatarSrc: me?.user_images?.[0]?.image?.url ?? null,
      isAdmin: me?.is_admin ?? null,
      isBroker: me?.is_broker ?? null,
      // https://www.intercom.com/help/en/articles/183-enable-identity-verification-for-web-and-mobile
      intercomHash: me?.intercom_hash ?? null,
      loggedInAs: loggedInAs ?? null,
    });
  }, [me]);

  return null;
};

// Identifies the session and sends a $pageview per path. Mounted once per
// signed-in BaseApp branch: the CRM layout, the gateway pages (onboarding
// wizard, claim, plans, noaccess) and the onboarding success page.
// People asking for their scraped profile to be taken down are not tracked.
const TAKEDOWN_PATH = /(^|\/)takedown(-agency)?\/?$/;

const AnalyticsTracker = () => {
  const { me, needsOnboarding, noAccess } = useAppData();
  const location = useLocation();
  const { identify, page, posthog } = useAnalytics();
  const { identifyUserId } = posthog;
  // A new agent's token is neither admin nor broker, so the Me query is
  // skipped for the whole wizard: only the user id is known.
  const tokenUserId = needsOnboarding?.userId ?? noAccess?.userId;
  const isTakedown = TAKEDOWN_PATH.test(location.pathname);

  React.useEffect(() => {
    if (isTakedown) {
      return;
    }
    if (me != null) {
      identify(me.id, { ...me, modules: getActiveModuleIds(me) });
      return;
    }
    if (tokenUserId != null && tokenUserId !== '') {
      identifyUserId(tokenUserId);
    }
  }, [isTakedown, me, tokenUserId, identify, identifyUserId]);

  React.useEffect(() => {
    if (!isTakedown) {
      page();
    }
  }, [isTakedown, location.pathname, page]);

  return null;
};

// Register MUIX license key
LicenseInfo.setLicenseKey(
  '94ebd1251d9b12888034f89d04c5f9f3Tz0xMjk2NjQsRT0xODExNjM1MTk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9UTEtMjAyNixRPTEsQVQ9bXVsdGksS1Y9Mg==',
);
muiXTelemetrySettings.disableTelemetry();

type BaseAppProps = {
  me: Me;
  noAccess?: { userId: string };
  needsOnboarding?: { userId: string };
};

// Whether the access token in hand stands for a signed-in user *right now*.
// A multitenant token carries no user yet — that session still owes us a
// tenant — so it belongs on the login card like anyone else.
const isSignedIn = () => {
  const accessToken = localStorage.getItem('access_token');
  if (accessToken == null) {
    return false;
  }
  try {
    const { user, exp } = parseJwt(accessToken);
    return (
      user?.id != null && typeof exp === 'number' && exp * 1000 > Date.now()
    );
  } catch {
    // An unparseable token is no session.
    return false;
  }
};

// The login card, unless the user is already signed in.
//
// Only a *hard load* of /login reaches this: in-app navigation keeps `me` and
// never renders the anonymous branch. An emailed or bookmarked login link
// lands here, and so does a post-auth redirect whose `return` points back at
// /login — which is how someone who has just authenticated ends up staring at
// the login card. `me` is null merely because AppDataProvider skips its query
// on /login, so the tokens are the only thing here that knows about the
// session.
const LoginRoute = () => {
  // The access token expires after an hour while the session behind it lives
  // for far longer, so "expired" is not an answer — anyone coming back to the
  // CRM after a lunch break holds a stale one. A refresh token means the
  // question can be settled, and settling it is one request the app was going
  // to make anyway.
  const [resolving, setResolving] = React.useState(
    () => !isSignedIn() && localStorage.getItem('refresh_token') != null,
  );

  React.useEffect(() => {
    if (!resolving) {
      return;
    }
    let cancelled = false;
    // Raced against a timer, and never rejecting into render: a refresh that
    // fails, or one that hangs on a dead socket (the fetch carries no timeout
    // of its own), only means we could not prove a session — and the login
    // card is the right answer for that. Without the race, `/login` would
    // render nothing for as long as the connection stalls, which is worse
    // than the flash this component exists to remove.
    Promise.race([
      refreshToken().catch(() => null),
      new Promise(resolve => setTimeout(resolve, 3000)),
    ]).then(() => {
      if (!cancelled) {
        setResolving(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [resolving]);

  if (resolving) {
    // Blank rather than the card: showing a login form to someone who turns
    // out to be signed in, for the length of one request, is the flash this
    // whole component exists to remove.
    return null;
  }

  if (!isSignedIn()) {
    return <LegacyLogin />;
  }

  // A full load rather than <Navigate>: AppDataProvider sits above the router
  // and computed `skip` from this pathname, so a client-side hop would keep
  // `me` null and bounce straight back here. Redirecting in render (as the
  // authenticated `/login` route does) interrupts everything else.
  window.location.replace(getAfterLoginDestination());
  return null;
};

// Sends a gated session to `to`, keeping where it was headed (e.g. a lead link
// from an email) so the claim page can go back there once claimed.
const RedirectWithReturn = ({ to }: { to: string }) => {
  const { pathname, search, hash } = useLocation();
  const params = new URLSearchParams({ return: pathname + search + hash });
  return <Navigate to={`${to}?${params}`} replace />;
};

// The magic-link redirect_uri always carries a lang prefix (e.g. /en/takedown),
// so every gated page exists under both /:lang/… and /…; anything else goes to
// `landing`. A function, not a component: <Routes> only accepts <Route> and
// fragments as children.
const gatewayRoutes = (
  landing: React.ReactElement,
  extraRoutes?: React.ReactNode,
  leadingRoutes?: React.ReactNode,
) => {
  const routes = (
    <>
      {/* Bare root (/, /:lang) matches these wrappers but has no segment to
          match a child; without an index it renders blank. Redirect to the
          landing page — the sibling `path="*"` is shadowed by `path="/"`. */}
      <Route index element={landing} />
      <Route path="login-email" element={<LoginEmail />} />
      <Route path="takedown" element={<TakedownAgent />} />
      <Route path="takedown-agency" element={<TakedownAgency />} />
      <Route path="claim" element={<ClaimProfile />} />
      <Route path="claim-agency" element={<ClaimAgency />} />
      {extraRoutes}
    </>
  );
  return (
    <>
      <AnalyticsTracker />
      <Routes>
        {leadingRoutes}
        <Route element={<GatewayLayout />}>
          <Route path="/:lang">{routes}</Route>
          <Route path="/">{routes}</Route>
          <Route path="*" element={landing} />
        </Route>
      </Routes>
    </>
  );
};

const BaseApp = ({ me, noAccess, needsOnboarding }: BaseAppProps) => {
  const location = useLocation();
  const loginParams = new URLSearchParams({ return: window.location.href });
  const currentUserId = needsOnboarding?.userId ?? me?.id ?? noAccess?.userId;
  const isOnboardingSuccessPath = location.pathname === '/onboarding/success';
  const completionSnapshot = isOnboardingSuccessPath
    ? readOnboardingCompletionForUser(currentUserId)
    : null;

  // Anonymous user
  if (me == null && noAccess == null && needsOnboarding == null) {
    return (
      <Routes>
        {localStorage.getItem('refresh_token') != null && (
          <Route path="/select-tenant" element={<SelectTenant />} />
        )}
        <Route path="/confirm-account" element={<InvitationAccept />} />
        <Route path="/login-email" element={<LoginEmail />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route
          path="*"
          element={
            <Navigate
              to={{
                pathname: '/login',
                search: loginParams.toString(),
              }}
              replace
            />
          }
        />
      </Routes>
    );
  }

  // The AI-agent OAuth consent page must be reachable in EVERY signed-in
  // state — trialing and no-access users would otherwise be silently
  // redirected mid-marketplace-flow, and the AI client waits forever for
  // a callback. The page is fully self-contained (own chrome, own auth
  // handling) and the chat service still gates who can actually grant.
  if (window.location.pathname.endsWith('/oauth/chat-consent')) {
    return (
      <React.Suspense fallback={null}>
        <Routes>
          <Route path="/oauth/chat-consent" element={<ChatOAuthConsent />} />
          <Route path="*" element={<ChatOAuthConsent />} />
        </Routes>
      </React.Suspense>
    );
  }

  if (completionSnapshot != null) {
    return (
      <>
        <AnalyticsTracker />
        <Routes>
          <Route
            path="/onboarding/*"
            element={
              <React.Suspense fallback={<SuspenseFallback fullPage />}>
                <OnboardingWizard
                  userId={
                    completionSnapshot?.userId ?? currentUserId ?? undefined
                  }
                />
              </React.Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </>
    );
  }

  if (needsOnboarding != null) {
    if (isOnboardingSuccessPath) {
      return (
        <Routes>
          <Route path="*" element={<Navigate to="/onboarding" replace />} />
        </Routes>
      );
    }

    return gatewayRoutes(
      <Navigate to="/onboarding" replace />,
      <>
        <Route
          path="noaccess"
          element={<NoAccess id={noAccess?.userId ?? me?.id} />}
        />
        <Route
          path="plans"
          element={
            <LegacyPlans
              tenantCountryCode={
                me?.tenant?.country_code ??
                getTenantSlugStageAndSubdomainFromHostname(
                  window.location.hostname,
                ).slug?.toUpperCase() ??
                'FR'
              }
              stripeCustomer={me?.subscriptions?.[0]?.customer}
              showPlansType="subscribe"
            />
          }
        />
      </>,
      <Route
        path="/onboarding/*"
        element={
          <React.Suspense fallback={<SuspenseFallback fullPage />}>
            <OnboardingWizard userId={needsOnboarding.userId} />
          </React.Suspense>
        }
      />,
    );
  }

  // User without access (not an admin or a broker)
  if (
    noAccess != null ||
    isNeitherAdminNorBroker(me?.is_admin, me?.is_broker)
  ) {
    return gatewayRoutes(
      <Navigate to="/noaccess" replace />,
      <>
        <Route
          path="noaccess"
          element={<NoAccess id={noAccess?.userId ?? me?.id} />}
        />
        <Route
          path="plans"
          element={
            <LegacyPlans
              tenantCountryCode={
                me?.tenant?.country_code ??
                getTenantSlugStageAndSubdomainFromHostname(
                  window.location.hostname,
                ).slug?.toUpperCase() ??
                'FR'
              }
              stripeCustomer={me?.subscriptions?.[0]?.customer}
              showPlansType="subscribe"
            />
          }
        />
      </>,
    );
  }

  // Scraped PT/PL profile never claimed: claim (or takedown) before anything
  // else. Must precede the show_plans branch — a trial or auto_assign_leads
  // sets show_plans='no' without claiming. Admins are excluded: the PT/PL seed
  // also flagged internal staff accounts.
  if (me?.is_unclaimed === true && me?.is_admin !== true) {
    return (
      <>
        <GTMReportUserDetails />
        {gatewayRoutes(<RedirectWithReturn to="/claim" />)}
      </>
    );
  }

  // User without subscription
  if (me?.show_plans != null && me?.show_plans !== 'no') {
    const plansElement =
      me?.show_plans === 'showTrialing' ? (
        <>
          <GTMReportUserDetails />
          <PlansTrialing />
        </>
      ) : (
        <>
          <GTMReportUserDetails />
          <LegacyPlans
            tenantCountryCode={me?.tenant?.country_code ?? 'FR'}
            stripeCustomer={me?.subscriptions?.[0]?.customer}
            showPlansType={me?.show_plans}
          />
        </>
      );
    return gatewayRoutes(
      <Navigate to="/plans" replace />,
      <Route path="plans" element={plansElement} />,
    );
  }

  const activeModules = getActiveModuleIds(me);
  // if module has leads or brokerage
  const isCRMActive = activeModules.some(
    m => m === MODULES_LEADS || m === MODULES_BROKERAGE,
  );
  const hasVisibilityModule = hasActiveModuleGrant(
    me?.module_grants,
    MODULES_VISIBILITY,
  );
  const defaultIndexPath =
    !isCRMActive && hasVisibilityModule ? 'visibility/overview' : 'dashboard';

  return (
    <React.Suspense
      fallback={
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CompassLoader size={80} />
        </div>
      }
    >
      <Routes>
        {/* Full-page routes — no sidebar/navbar */}
        <Route path="/login-email" element={<LoginEmail />} />
        {/* All other routes with sidebar/navbar layout */}
        <Route
          path="/*"
          element={
            <Layout>
              <BookADemoBanner />
              <OnboardingStatusBanner />
              <GTMReportUserDetails />
              <AnalyticsTracker />
              <React.Suspense fallback={<SuspenseFallback />}>
                <Routes>
                  <Route
                    path="/onboarding/*"
                    element={<Navigate to={`/${defaultIndexPath}`} replace />}
                  />
                  <Route path="/dashboard/*" element={<Dashboard />} />
                  <Route
                    path="/v2/maps/*"
                    element={
                      <RedirectKeepingQuery to={LEGACY_PROSPECTION_MAP_PATH} />
                    }
                  />
                  <Route
                    path="/v2/prospection/*"
                    element={
                      <RedirectKeepingQuery to={PROSPECTION_MAP_V2_PATH} />
                    }
                  />
                  {/* Buyer leads are for every tenant admin. The rest of /v2
                      stays RealAdvisor admins only. */}
                  <Route element={<AdminRoute />}>
                    <Route path="/v2/buyer-leads/*" element={<BuyerLeads />} />
                    <Route element={<RATenantRoute />}>
                      <Route path="/v2/*" element={<V2Routes />} />
                    </Route>
                  </Route>
                  <Route element={<ProspectionMapRoute map="legacy" />}>
                    <Route
                      path={`${LEGACY_PROSPECTION_MAP_PATH}/*`}
                      element={<ProspectionMap />}
                    />
                  </Route>
                  <Route element={<ProspectionMapRoute map="v2" />}>
                    <Route
                      path={`${PROSPECTION_MAP_V2_PATH}/*`}
                      element={<ProspectionMapV2 />}
                    />
                  </Route>
                  <Route
                    path="/confirm-account"
                    element={<InvitationAccept />}
                  />
                  <Route path="/teams/*" element={<Teams />} />
                  <Route
                    path="/brochure-reports/:listingId/:pageId/*"
                    element={<BrochureReportEditor />}
                  >
                    <Route path=":imgId" />
                  </Route>
                  <Route path="/listings/*" element={<Listings />} />
                  <Route element={<ModuleRoute module={MODULES_BROKERAGE} />}>
                    <Route path="/mls/*" element={<MLS />} />
                  </Route>
                  <Route path="/enquiries/*" element={<Enquiries />} />
                  <Route
                    path="/property-searches/*"
                    element={<PropertySearches />}
                  />
                  <Route path="/leads/*" element={<Leads />} />
                  <Route
                    element={<ModuleRoute module={MODULES_VALUATION_LINK} />}
                  >
                    <Route
                      path="/valuation-links/*"
                      element={<ValuationLinks />}
                    />
                  </Route>
                  <Route path="/users/*" element={<Users />} />
                  <Route path="/developments/*" element={<Developments />} />
                  <Route element={<AdminRoute />}>
                    <Route
                      path="/organisations/*"
                      element={<Organisations />}
                    />
                  </Route>
                  <Route path="/transactions/*" element={<Transactions />} />
                  <Route
                    path="/performance/activities/*"
                    element={<PerformanceActivities />}
                  />
                  <Route path="/offers/*" element={<Offers />} />
                  <Route element={<ModuleRoute module={MODULES_VISIBILITY} />}>
                    <Route
                      path="/visibility/overview/*"
                      element={<VisibilityDashboard />}
                    />
                    <Route
                      path="/visibility/statistics"
                      element={<VisibilityStats />}
                    />
                    <Route
                      path="/visibility/transactions/*"
                      element={<VisibilityTransactions />}
                    />
                    <Route
                      path="/visibility/inbox/*"
                      element={<VisibilityInbox />}
                    />
                    <Route
                      path="/visibility/reviews/*"
                      element={<VisibilityReviews />}
                    />
                    <Route
                      path="/visibility/team/*"
                      element={<VisibilityTeam />}
                    />
                  </Route>
                  <Route element={<ModuleRoute module={MODULES_CMA_REPORTS} />}>
                    <Route
                      path="/cma-reports/:cmaReportId/:pageId/*"
                      element={<CMAReportEditor />}
                    />
                    <Route path="/cma-reports/*" element={<CMAReportsList />}>
                      <Route path="new/*" />
                    </Route>
                    <Route
                      path="/comparable-listings/*"
                      element={<ComparableListings />}
                    />
                  </Route>
                  <Route element={<AdminRoute />}>
                    <Route path="/reviews/*" element={<Reviews />} />
                  </Route>
                  <Route path="/docs/*" element={<DocsRoute />} />
                  <Route path="/whats-new" element={<WhatsNewPage />} />
                  <Route
                    path="/whats-new/:releaseId"
                    element={<WhatsNewPage />}
                  />
                  <Route path="/settings/*" element={<SettingsRoutes />} />
                  {/* Routes in development */}

                  {/* Legacy routes */}
                  <Route path="/*" element={<LegacyRoutes />} />
                  {/* Default routes */}
                  <Route index element={<Navigate to={defaultIndexPath} />} />
                </Routes>
              </React.Suspense>
            </Layout>
          }
        />
      </Routes>
    </React.Suspense>
  );
};

type LocaleInitializerProps = {
  children: React.ReactNode;
};

const LocaleInitializer = ({ children }: LocaleInitializerProps) => {
  const { dateLocale } = useLocale();
  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={dateLocale}
    >
      {children}
    </LocalizationProvider>
  );
};

export const App = () => {
  const { me, noAccess, needsOnboarding } = useAppData();

  // prevent changing ui language when user changes it in own profile
  const userLanguage = useConstant(() => getLanguage(me?.language));
  const urlLanguage = useConstant(() => extractLanguageFromUrl());
  const currentLanguage = userLanguage ?? urlLanguage ?? fallbackLanguage;

  // redirect when laguage not found in url
  // or when it's not matched with user language
  if (urlLanguage !== currentLanguage) {
    const pathname = replacePathnameLanguage(
      location.pathname,
      currentLanguage,
    );
    // redirect in render to interrupt everything else
    // preserve table filters in search and anchor hash (e.g. mail CTAs)
    location.href = `${pathname}${location.search}${location.hash}`;
    return null;
  }

  return (
    <>
      {/* show errors only on test server, or in dev mode or if user is admin */}
      {(import.meta.env.NODE_ENV !== 'production' ||
        !isProduction ||
        me?.is_admin === true) && (
        <ErrorReporting errors={[]} emitter={errorsEmitter} />
      )}
      <React.Suspense fallback={null}>
        <SchemaInitializer />
      </React.Suspense>
      <ImageProvider>
        <BrowserRouter basename={`/${currentLanguage}`}>
          {/* locale provider should be upper to prevent conflicting with app mui style provider */}
          <LocaleProvider
            currentLanguage={currentLanguage}
            countryCode={me?.tenant?.country_code ?? 'CH'}
          >
            <LocaleInitializer>
              <NavigationProvider>
                <StyleProvider>
                  <GoogleKeyProvider googleKey={GOOGLE_MAPS_TOKEN}>
                    <ProductUpdatesProvider>
                      <BaseApp
                        me={me}
                        noAccess={noAccess}
                        needsOnboarding={needsOnboarding}
                      />
                    </ProductUpdatesProvider>
                  </GoogleKeyProvider>
                </StyleProvider>
              </NavigationProvider>
            </LocaleInitializer>
          </LocaleProvider>
        </BrowserRouter>
      </ImageProvider>
    </>
  );
};
